/**
 * resolveEmail — RPC contract for the sign-in's email-resolution step, an
 * action on the `identityOtp` router (`identityOtp.action.ts`).
 *
 * Cross-process contract between `packages/session/src/session.api.ts`
 * (caller) and `firebase/src/auth/resolveEmail.api.ts` (handler). Both ends
 * parse with `.strict()` — an extra key fails the whole request.
 *
 * The FIRST step of the sign-in: the person types an address, this answers
 * whether `sendOTP` would mail it a code, and the view branches — `known`
 * goes to the code step after a `sendOTP`; unknown sees the no-account
 * prompt and no code is ever asked for. `sendOTP` keeps its own admission;
 * this decides only which screen comes next.
 *
 * A DELIBERATE, BOUNDED EXISTENCE ORACLE. `SendOTPOutput.sent` exists so
 * the server never says whether an address is admitted; `known` says exactly
 * that, by the owner's decision: a returning person should not be left
 * guessing whether they have an account, and a stranger should not be told
 * "code sent" about a mail that never goes out. Bounded, not open: the
 * handler enforces App Check outside the emulator and throttles per caller
 * IP (`resolveEmail.api.ts`'s header), and `known` is computed by the one
 * admission `sendOTP` itself runs (`firebase/src/auth/admission.api.ts`), so
 * the two cannot disagree about who gets a code.
 *
 * Fields justified:
 *   orgId  — the organization whose sign-in this is, exactly as
 *             `SendOTPInput.orgId`: its members and invitees are admitted,
 *             and an address it does not admit but the platform does is
 *             admitted platform-wide. Absent means platform scope, the bare
 *             host's sign-in. Nullish for the same reason as sendOTP's.
 *   email  — the address to resolve; the same RFC-5321 validation as
 *             `SendOTPInput.email`, so the handler never sees a malformed one.
 *
 * Output:
 *   known  — `true` when `sendOTP` would mail this address a code under the
 *             same `orgId`: a member of the organization, a live pending
 *             invite, or a platform admission (a grant anywhere, a listed
 *             domain).
 *   invite — the IDENTICAL display projection `SendOTPOutput.invite`
 *             carries, for the identical pending invite, resolved by the
 *             identical shared admission; `null` when none names this address,
 *             including when `known` is `true` for a membership reason.
 *
 * Excluded:
 *   locale — nothing is mailed here.
 *   userId — the caller is unauthenticated; no session.
 */
import { z } from "zod";
import { InviteProjectionSchema } from "./invite.entity.ts";
import { Id } from "./workspace.entity.ts";

export const ResolveEmailInput = z
	.object({
		orgId: Id.nullish(),
		email: z
			.string()
			.email()
			.describe(
				"the address to resolve before the sign-in chooses its next screen",
			),
	})
	.strict();
export type ResolveEmailInput = z.infer<typeof ResolveEmailInput>;

export const ResolveEmailOutput = z
	.object({
		known: z
			.boolean()
			.describe(
				"whether sendOTP would mail this address a code under the same scope — the one shared admission's answer",
			),
		invite: InviteProjectionSchema.nullish().describe(
			"present only when this email holds a live pending invite; the same shape sendOTP returns for the same address",
		),
	})
	.strict();
export type ResolveEmailOutput = z.infer<typeof ResolveEmailOutput>;
